import type React from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FontPicker from '../../inputs/FontPicker/FontPicker';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import useCanvasHistory from '../hooks/useCanvasHistory';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationType, CanvasImage, TextItem } from '../types';

const FONT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 36, 40, 48, 56, 64, 72, 96, 120];
const LINE_HEIGHTS = [0.8, 1.0, 1.2, 1.4, 1.5, 1.6, 1.8, 2.0];
const LETTER_SPACINGS = [-2, -1.5, -1, -0.5, 0, 0.5, 1, 1.5, 2, 3, 4, 5];

const ANIMATIONS = {
  entrance: [
    { value: 'simple-fade', label: 'Fade In' },
    { value: 'slide-from-left', label: 'Slide From Left' },
    { value: 'slide-from-right', label: 'Slide From Right' },
    { value: 'slide-from-bottom', label: 'Slide From Bottom' },
    { value: 'slide-from-top', label: 'Slide From Top' },
    { value: 'move', label: 'Move In' },
    { value: 'grow', label: 'Grow' },
    { value: 'shrink', label: 'Shrink' },
    { value: 'blur-in', label: 'Blur In' },
  ],
  loop: [
    { value: 'fade-in', label: 'Pulsing Fade' },
    { value: 'slide-up', label: 'Floating' },
    { value: 'zoom-in', label: 'Pulsing Size' },
    { value: 'rotate', label: 'Rotating' },
    { value: 'bounce', label: 'Bouncing' },
    { value: 'color-pulse', label: 'Color Pulse' },
    { value: 'pulse', label: 'Pulse' },
    { value: 'shake', label: 'Shake' },
    { value: 'spin', label: 'Spin' },
    { value: 'swing', label: 'Swing' },
    { value: 'float', label: 'Float' },
    { value: 'wiggle', label: 'Wiggle' },
    { value: 'flicker', label: 'Flicker' },
  ],
  special: [
    { value: 'flip-horizontal', label: 'Flip Horizontal' },
    { value: 'flip-vertical', label: 'Flip Vertical' },
    { value: 'rubber-band', label: 'Rubber Band' },
  ],
};

const CanvasControls: React.FC = () => {
  const texts = useCanvasStore((s) => s.texts);
  const selectedId = useCanvasStore((s) => s.selectedId);
  const backgroundColor = useCanvasStore((s) => s.backgroundColor);
  const setBackgroundColor = useCanvasStore((s) => s.setBackgroundColor);
  const setBgImage = useCanvasStore((s) => s.setBgImage);
  const setSelectedId = useCanvasStore((s) => s.setSelectedId);
  const previewMode = useCanvasStore((s) => s.previewMode);
  const bgImage = useCanvasStore((s) => s.bgImage);
  const setTexts = useCanvasStore((s) => s.setTexts);
  const saveStateToHistory = useCanvasStore((s) => s.saveStateToHistory);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const updateImage = useCanvasStore((s) => s.updateImage);
  const { saveHistory } = useCanvasHistory();

  const selectedText = useMemo(() => {
    if (!texts || !selectedId || typeof selectedId !== 'string') return null;
    return texts.find((t) => t.id === selectedId) || null;
  }, [selectedId, texts]);

  const selectedImage = useMemo(() => {
    if (!selectedId || selectedId === 'logo' || selectedId === 'canvas') return null;
    return canvasImages.find((img) => img.id === selectedId);
  }, [selectedId, canvasImages]);

  const updateTextProperty = useCallback(
    <K extends keyof TextItem>(property: K, value: TextItem[K]) => {
      setTexts((prev) => prev.map((t) => (t.id === selectedId ? { ...t, [property]: value } : t)));
    },
    [selectedId, setTexts]
  );

  const animationOptions = useMemo(
    () => (
      <>
        <option value="none">None</option>
        <optgroup label="Loop Animations">
          {ANIMATIONS.loop.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Entrance Animations">
          {ANIMATIONS.entrance.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Special Animations">
          {ANIMATIONS.special.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </optgroup>
      </>
    ),
    []
  );

  const handleBackgroundColorChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setBackgroundColor(e.target.value);
    },
    [setBackgroundColor]
  );

  const handleRemoveBg = useCallback(() => {
    setBgImage(null);
  }, [setBgImage]);

  const getTextPropertyHandler = useCallback(
    <K extends keyof TextItem>(property: K) =>
      (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        saveStateToHistory();
        const value =
          property === 'fontSize' || property === 'lineHeight' || property === 'letterSpacing'
            ? Number(e.target.value)
            : e.target.value;
        updateTextProperty(property, value as TextItem[K]);
      },
    [updateTextProperty, saveStateToHistory]
  );

  const handleTextColorChange = (color: string) => {
    saveStateToHistory();
    updateTextProperty('color', color);
  };

  const handleImageOpacityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedImage) return;

      saveHistory();
      const value = Number.parseFloat(e.target.value);
      updateImage(selectedId as string, { opacity: value } as Partial<CanvasImage>);
    },
    [selectedId, selectedImage, updateImage, saveHistory]
  );

  const handleAnimationChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      saveHistory();
      const value = e.target.value as AnimationType;

      if (selectedText) {
        updateTextProperty('animation', value);
        return;
      }

      if (selectedImage) {
        updateImage(selectedId as string, { animation: value });
      }
    },
    [selectedId, selectedText, selectedImage, updateTextProperty, updateImage, saveHistory]
  );

  const handleAnimationPropertyChange = useCallback(
    (property: 'animationDelay' | 'animationDuration', e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedImage) return;

      saveHistory();
      const rawValue = Number(e.target.value);
      const value = Number.isNaN(rawValue) ? 0 : Math.max(0, Math.min(property === 'animationDelay' ? 3 : 5, rawValue));

      updateImage(selectedId as string, { [property]: value });
    },
    [selectedId, selectedImage, updateImage, saveHistory]
  );

  const handleTextAnimationPropertyChange = useCallback(
    (property: 'animationDelay' | 'animationDuration', e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedText) return;

      saveHistory();
      const rawValue = Number(e.target.value);
      const value = Number.isNaN(rawValue) ? 0 : Math.max(0, Math.min(property === 'animationDelay' ? 3 : 5, rawValue));

      updateTextProperty(property, value);
    },
    [selectedText, updateTextProperty, saveHistory]
  );

  const bgColorInputRef = useRef<HTMLInputElement>(null);
  const textColorInputRef = useRef<HTMLInputElement>(null);

  const formatToHex = useCallback((color: string) => {
    if (color.startsWith('#')) return color;
    return `#${color}`;
  }, []);

  const openColorPicker = useCallback((inputRef: React.RefObject<HTMLInputElement | null>) => {
    if (inputRef.current) {
      inputRef.current.setAttribute('data-format', 'hex');
      inputRef.current.click();
    }
  }, []);

  const canvasControls = useMemo(
    () =>
      selectedId === 'canvas' ? (
        <div className="edit-controls zngly-controls-panel">
          <div className="control-group">
            <label htmlFor="bg-color-input" className="control-label">
              Background Color:
            </label>
            <div className="color-picker-container">
              <div
                className="color-picker"
                style={{
                  backgroundColor,
                  width: '36px',
                  height: '36px',
                  cursor: 'pointer',
                }}
                onClick={() => openColorPicker(bgColorInputRef)}
              />
              <input
                type="text"
                value={backgroundColor}
                onChange={(e) => {
                  saveStateToHistory();
                  setBackgroundColor(formatToHex(e.target.value));
                }}
                className="hex-color-input"
              />
              <input
                ref={bgColorInputRef}
                id="bg-color-input"
                type="color"
                value={backgroundColor}
                onChange={handleBackgroundColorChange}
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
              />
            </div>
          </div>
          {bgImage && (
            <div className="control-group">
              <button
                type="button"
                className="zngly-button zngly-button-tertiary zngly-button-sm"
                onClick={handleRemoveBg}
              >
                Remove Background Image
              </button>
            </div>
          )}
        </div>
      ) : null,
    [
      selectedId,
      backgroundColor,
      bgImage,
      handleBackgroundColorChange,
      handleRemoveBg,
      openColorPicker,
      formatToHex,
      saveStateToHistory,
    ]
  );

  const [fontSizeDropdownOpen, setFontSizeDropdownOpen] = useState(false);
  const fontSizeInputRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fontSizeDropdownOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (fontSizeInputRef.current && !fontSizeInputRef.current.contains(e.target as Node)) {
        setFontSizeDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fontSizeDropdownOpen]);

  const handleCustomFontSizeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!selectedText) return;

      saveStateToHistory();
      const value = e.target.value;

      if (value === '') return;

      const numValue = Number.parseInt(value, 10);
      if (!Number.isNaN(numValue)) {
        const clampedSize = Math.max(6, Math.min(500, numValue));
        updateTextProperty('fontSize', clampedSize);
      }
    },
    [selectedText, saveStateToHistory, updateTextProperty]
  );

  const textControls = useMemo(() => {
    if (!selectedText) return null;

    return (
      <div className="edit-controls zngly-controls-panel">
        <div className="control-group">
          <label htmlFor="text-color-input" className="control-label">
            Text Color:
          </label>
          <div className="color-picker-container">
            <div
              className="color-picker"
              style={{
                backgroundColor: selectedText.color,
                width: '36px',
                height: '36px',
                cursor: 'pointer',
              }}
              onClick={() => openColorPicker(textColorInputRef)}
            />
            <input
              type="text"
              value={selectedText.color}
              onChange={(e) => {
                saveStateToHistory();
                handleTextColorChange(formatToHex(e.target.value));
              }}
              className="hex-color-input"
            />
            <input
              ref={textColorInputRef}
              id="text-color-input"
              type="color"
              value={selectedText.color}
              onChange={(e) => handleTextColorChange(e.target.value)}
              style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
            />
          </div>
        </div>

        <div className="control-group">
          <label htmlFor="font-family-select" className="control-label">
            Font:
          </label>
          <div style={{ width: '100%' }} className="font-selector">
            <FontPicker
              font={selectedText.fontFamily}
              onChange={(value: string | null) => {
                if (value) {
                  saveStateToHistory();
                  updateTextProperty('fontFamily', value);
                }
              }}
              onBlur={() => {}}
            />
          </div>
        </div>

        <div className="control-group">
          <label htmlFor="custom-font-size" className="control-label">
            Font Size:
          </label>
          <div
            ref={fontSizeInputRef}
            className="custom-font-size-control"
            style={{
              position: 'relative',
              display: 'flex',
              width: '100%',
            }}
          >
            <input
              id="custom-font-size"
              type="text"
              className="select-input"
              value={selectedText.fontSize}
              onChange={handleCustomFontSizeChange}
              onBlur={(e) => {
                if (e.target.value === '' || Number.isNaN(Number.parseInt(e.target.value, 10))) {
                  updateTextProperty('fontSize', 24); // Default to 24 if invalid
                }
              }}
              style={{
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                width: 'calc(100% - 32px)',
                paddingRight: '26px',
              }}
              disabled={previewMode}
            />
            <div
              className="font-size-unit"
              style={{
                position: 'absolute',
                right: '36px',
                top: '50%',
                transform: 'translateY(-50%)',
                fontSize: '12px',
                color: '#666',
              }}
            >
              px
            </div>
            <button
              type="button"
              onClick={() => setFontSizeDropdownOpen(!fontSizeDropdownOpen)}
              style={{
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '1px solid var(--zngly-border)',
                borderLeft: 'none',
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                borderTopRightRadius: '4px',
                borderBottomRightRadius: '4px',
                backgroundColor: 'white',
                cursor: 'pointer',
              }}
              disabled={previewMode}
            >
              ▼
            </button>

            {fontSizeDropdownOpen && (
              <div
                className="font-size-dropdown"
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 1000,
                  backgroundColor: 'white',
                  border: '1px solid var(--zngly-border)',
                  borderRadius: '0 0 4px 4px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                }}
              >
                {FONT_SIZES.map((size) => (
                  <div
                    key={size}
                    onClick={() => {
                      saveStateToHistory();
                      updateTextProperty('fontSize', size);
                      setFontSizeDropdownOpen(false);
                    }}
                    style={{
                      padding: '8px 12px',
                      cursor: 'pointer',
                      backgroundColor: selectedText.fontSize === size ? '#f0f9ff' : 'white',
                      borderBottom: '1px solid #eee',
                    }}
                  >
                    {size}px
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="control-group">
          <label htmlFor="font-style-select" className="control-label">
            Font Style:
          </label>
          <select
            id="font-style-select"
            className="select-input"
            value={
              selectedText.italic
                ? selectedText.fontWeight === 'bold' || selectedText.fontWeight === '700'
                  ? 'bold-italic'
                  : 'italic'
                : selectedText.fontWeight === 'bold' || selectedText.fontWeight === '700'
                  ? 'bold'
                  : 'normal'
            }
            onChange={(e) => {
              saveStateToHistory();
              const value = e.target.value;
              switch (value) {
                case 'bold':
                  updateTextProperty('fontWeight', 'bold');
                  updateTextProperty('italic', false);
                  break;
                case 'italic':
                  updateTextProperty('fontWeight', 'normal');
                  updateTextProperty('italic', true);
                  break;
                case 'bold-italic':
                  updateTextProperty('fontWeight', 'bold');
                  updateTextProperty('italic', true);
                  break;
                default:
                  updateTextProperty('fontWeight', 'normal');
                  updateTextProperty('italic', false);
              }
            }}
            disabled={previewMode}
          >
            <option value="normal">Normal</option>
            <option value="bold">Bold</option>
            <option value="italic">Italic</option>
            <option value="bold-italic">Bold Italic</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="line-height-select" className="control-label">
            Line Height:
          </label>
          <select
            id="line-height-select"
            className="select-input"
            value={selectedText.lineHeight}
            onChange={getTextPropertyHandler('lineHeight')}
            disabled={previewMode}
          >
            {LINE_HEIGHTS.map((height) => (
              <option key={height} value={height}>
                {height}
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="letter-spacing-select" className="control-label">
            Letter Spacing:
          </label>
          <select
            id="letter-spacing-select"
            className="select-input"
            value={selectedText.letterSpacing}
            onChange={getTextPropertyHandler('letterSpacing')}
            disabled={previewMode}
          >
            {LETTER_SPACINGS.map((spacing) => (
              <option key={spacing} value={spacing}>
                {spacing}px
              </option>
            ))}
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="text-align-select" className="control-label">
            Align:
          </label>
          <select
            id="text-align-select"
            className="select-input"
            value={selectedText.align}
            onChange={getTextPropertyHandler('align')}
            disabled={previewMode}
          >
            <option value="left">Left</option>
            <option value="center">Center</option>
            <option value="right">Right</option>
          </select>
        </div>

        <div className="control-group">
          <label htmlFor="animation-select" className="control-label">
            Animation:
          </label>
          <select
            id="animation-select"
            className="select-input"
            value={selectedText.animation || 'none'}
            onChange={handleAnimationChange}
            disabled={previewMode}
          >
            {animationOptions}
          </select>
        </div>

        {selectedText.animation && selectedText.animation !== 'none' && (
          <>
            <div className="control-group">
              <label htmlFor="text-animation-duration" className="control-label">
                Duration:
              </label>
              <div className="range-with-value">
                <input
                  type="range"
                  id="text-animation-duration"
                  min="0.5"
                  max="5"
                  step="0.1"
                  value={selectedText.animationDuration || 1}
                  onChange={(e) => handleTextAnimationPropertyChange('animationDuration', e)}
                  className="range-input"
                  disabled={previewMode}
                  aria-valuemin={0.5}
                  aria-valuemax={5}
                  aria-valuenow={selectedText.animationDuration || 1}
                  aria-valuetext={`${selectedText.animationDuration || 1} seconds`}
                />
                <span className="range-value">{selectedText.animationDuration || 1}s</span>
              </div>
            </div>

            <div className="control-group">
              <label htmlFor="text-animation-delay" className="control-label">
                Delay:
              </label>
              <div className="range-with-value">
                <input
                  type="range"
                  id="text-animation-delay"
                  min="0"
                  max="3"
                  step="0.1"
                  value={selectedText.animationDelay || 0}
                  onChange={(e) => handleTextAnimationPropertyChange('animationDelay', e)}
                  className="range-input"
                  disabled={previewMode}
                  aria-valuemin={0}
                  aria-valuemax={3}
                  aria-valuenow={selectedText.animationDelay || 0}
                  aria-valuetext={`${selectedText.animationDelay || 0} seconds`}
                />
                <span className="range-value">{selectedText.animationDelay || 0}s</span>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }, [
    selectedText,
    previewMode,
    getTextPropertyHandler,
    handleTextColorChange,
    handleAnimationChange,
    handleTextAnimationPropertyChange,
    handleCustomFontSizeChange, // Add to dependency array
    animationOptions,
    saveStateToHistory,
    openColorPicker,
    formatToHex,
    fontSizeDropdownOpen, // Add to dependency array
    updateTextProperty, // Add to dependency array
  ]);

  const imageControls = useMemo(() => {
    if (!selectedImage) return null;

    const imageOpacity = selectedImage.opacity ?? 1;
    const imageAnimation = selectedImage.animation || 'none';
    const animationDelay = selectedImage.animationDelay ?? 0;
    const animationDuration = selectedImage.animationDuration ?? 1;

    return (
      <fieldset className="edit-controls zngly-controls-panel">
        <legend>Image controls</legend>

        <div className="control-group">
          <label className="control-label" htmlFor="image-width">
            Dimensions:
          </label>
          <div
            className="dimensions-control"
            style={{
              display: 'flex',
              gap: '2px',
              flexDirection: 'row',
              width: '100%',
              marginBottom: '4px',
            }}
          >
            <div
              className="dimension-input-group"
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                height: '28px',
                maxWidth: '48%',
              }}
            >
              <label
                htmlFor="image-width"
                className="dimension-label"
                style={{
                  marginRight: '1px',
                  width: '12px',
                  fontSize: '11px',
                  color: '#666',
                }}
              >
                W:
              </label>
              <input
                type="number"
                id="image-width"
                min="10"
                value={Math.round(selectedImage.width)}
                onChange={(e) => {
                  saveStateToHistory();
                  const oldWidth = selectedImage.width;
                  const newWidth = Math.max(10, Math.min(CANVAS_WIDTH * 2, Number.parseInt(e.target.value) || 10));

                  const centerX = selectedImage.x + oldWidth / 2;
                  let newX = centerX - newWidth / 2;

                  if (newX < 0) {
                    newX = 0;
                  } else if (newX + newWidth > CANVAS_WIDTH) {
                    if (newWidth <= CANVAS_WIDTH) {
                      newX = CANVAS_WIDTH - newWidth;
                    } else {
                      newX = 0;
                    }
                  }

                  updateImage(selectedId as string, {
                    width: newWidth,
                    x: newX,
                  });
                }}
                disabled={previewMode}
                className="dimension-input"
                style={{
                  flex: '1',
                  minWidth: '30px',
                  maxWidth: 'calc(100% - 14px)',
                  height: '26px',
                  padding: '1px 2px',
                  border: '1px solid var(--zngly-border)',
                  borderRadius: '3px',
                  fontSize: '11px',
                }}
              />
            </div>
            <div
              className="dimension-input-group"
              style={{
                flex: '1',
                display: 'flex',
                alignItems: 'center',
                height: '28px',
                maxWidth: '48%',
              }}
            >
              <label
                htmlFor="image-height"
                className="dimension-label"
                style={{
                  marginRight: '1px',
                  width: '12px',
                  fontSize: '11px',
                  color: '#666',
                }}
              >
                H:
              </label>
              <input
                type="number"
                id="image-height"
                min="10"
                value={Math.round(selectedImage.height)}
                onChange={(e) => {
                  saveStateToHistory();
                  const oldHeight = selectedImage.height;
                  const newHeight = Math.max(10, Math.min(CANVAS_HEIGHT * 2, Number.parseInt(e.target.value) || 10));

                  const centerY = selectedImage.y + oldHeight / 2;
                  let newY = centerY - newHeight / 2;

                  if (newY < 0) {
                    newY = 0;
                  } else if (newY + newHeight > CANVAS_HEIGHT) {
                    if (newHeight <= CANVAS_HEIGHT) {
                      newY = CANVAS_HEIGHT - newHeight;
                    } else {
                      newY = 0;
                    }
                  }

                  updateImage(selectedId as string, {
                    height: newHeight,
                    y: newY,
                  });
                }}
                disabled={previewMode}
                className="dimension-input"
                style={{
                  flex: '1',
                  minWidth: '30px',
                  maxWidth: 'calc(100% - 14px)',
                  height: '26px',
                  padding: '1px 2px',
                  border: '1px solid var(--zngly-border)',
                  borderRadius: '3px',
                  fontSize: '11px',
                }}
              />
            </div>
          </div>

          <div className="maintain-aspect-ratio" style={{ marginTop: '8px' }}>
            <button
              type="button"
              className="zngly-button zngly-button-tertiary zngly-button-sm"
              onClick={() => {
                const img = new Image();
                img.onload = () => {
                  const aspectRatio = img.width / img.height;
                  const currentWidth = selectedImage.width;
                  const maxWidth = Math.min(CANVAS_WIDTH * 0.8, 600);

                  const newWidth = Math.min(currentWidth, maxWidth);
                  const newHeight = newWidth / aspectRatio;

                  const newX = selectedImage.x + (selectedImage.width - newWidth) / 2;
                  const newY = selectedImage.y + (selectedImage.height - newHeight) / 2;

                  updateImage(selectedId as string, {
                    width: newWidth,
                    height: newHeight,
                    x: newX,
                    y: newY,
                  });
                };
                img.src = selectedImage.url;
              }}
              disabled={previewMode}
            >
              Reset Size
            </button>
          </div>
        </div>

        <div className="control-group">
          <label className="control-label" htmlFor="image-opacity">
            Opacity:
          </label>
          <div className="range-with-value">
            <input
              type="range"
              id="image-opacity"
              min="0.1"
              max="1"
              step="0.1"
              value={imageOpacity}
              onChange={handleImageOpacityChange}
              className="range-input"
              disabled={previewMode}
              aria-valuemin={0.1}
              aria-valuemax={1}
              aria-valuenow={imageOpacity}
              aria-valuetext={`${Math.round(imageOpacity * 100)}%`}
            />
            <span className="range-value">{Math.round(imageOpacity * 100)}%</span>
          </div>
        </div>

        <div className="control-group">
          <label className="control-label" htmlFor="image-animation">
            Animation:
          </label>
          <select
            id="image-animation"
            value={imageAnimation}
            onChange={handleAnimationChange}
            className="select-input"
            disabled={previewMode}
          >
            {animationOptions}
          </select>
        </div>

        {imageAnimation !== 'none' && (
          <>
            <div className="control-group">
              <label className="control-label" htmlFor="animation-delay">
                Delay (s):
              </label>
              <div className="range-with-value">
                <input
                  type="range"
                  id="animation-delay"
                  min="0"
                  max="3"
                  step="0.1"
                  value={animationDelay}
                  onChange={(e) => handleAnimationPropertyChange('animationDelay', e)}
                  className="range-input"
                  disabled={previewMode}
                  aria-valuemin={0}
                  aria-valuemax={3}
                  aria-valuenow={animationDelay}
                  aria-valuetext={`${animationDelay} seconds`}
                />
                <span className="range-value">{animationDelay}s</span>
              </div>
            </div>

            <div className="control-group">
              <label className="control-label" htmlFor="animation-duration">
                Duration (s):
              </label>
              <div className="range-with-value">
                <input
                  type="range"
                  id="animation-duration"
                  min="0.5"
                  max="5"
                  step="0.1"
                  value={animationDuration}
                  onChange={(e) => handleAnimationPropertyChange('animationDuration', e)}
                  className="range-input"
                  disabled={previewMode}
                  aria-valuemin={0.5}
                  aria-valuemax={5}
                  aria-valuenow={animationDuration}
                  aria-valuetext={`${animationDuration} seconds`}
                />
                <span className="range-value">{animationDuration}s</span>
              </div>
            </div>
          </>
        )}

        <div className="control-group">
          <div className="control-label" id="image-controls-label">
            Image Controls:
          </div>
          <div className="inline-controls" aria-labelledby="image-controls-label">
            <button
              type="button"
              className="zngly-button zngly-button-danger zngly-button-sm"
              onClick={() => {
                saveStateToHistory();
                const id = selectedId as string;
                setSelectedId(null);
                setTimeout(() => updateImage(id, { deleted: true }), 0);
              }}
              disabled={previewMode}
              aria-label="Remove image"
            >
              Remove Image
            </button>
          </div>
        </div>
      </fieldset>
    );
  }, [
    selectedImage,
    selectedId,
    previewMode,
    handleImageOpacityChange,
    handleAnimationChange,
    handleAnimationPropertyChange,
    animationOptions,
    saveStateToHistory,
    updateImage,
    setSelectedId,
  ]);

  if (!selectedId || selectedId === 'canvas') {
    return canvasControls;
  }

  if (selectedText) {
    return textControls;
  }

  if (selectedImage) {
    return imageControls;
  }

  return null;
};

export default memo(CanvasControls);
